import type { ReactNode } from 'react';
import { Layout as RALayout, AppBar, TitlePortal, Sidebar, useSidebarState, MenuItemLink, useResourceDefinitions, UserMenu, Logout } from 'react-admin';
import Box from '@mui/material/Box';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { Divider, ListSubheader } from '@mui/material';
import { useState, useEffect } from 'react';
import { CanAccess } from 'react-admin';
import { useLocation, Link } from 'react-router-dom';
import { resources } from '@repo/c360-ra-components';
import { CanAccessAny, SECTION_RESOURCES } from './utils/canAccessAny';
import {
  Payment as PaymentIcon,
  Payments as PaymentsIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Person as PersonIcon,
  Book as BookIcon,
  Security as SecurityIcon,
  Badge as BadgeIcon,
  History as HistoryIcon,
  Policy as PolicyIcon,
  Help as HelpIcon,
  CreditCard as CreditCardIcon,
  Description as DescriptionIcon,
  PersonAdd as PersonAddIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Event as EventIcon,
  Receipt as ReceiptIcon,
  ReceiptLong as ReceiptLongIcon,
  FindInPage as FindInPageIcon,
  SyncAlt as SyncAltIcon,
  CreditCardOff as CreditCardOffIcon,
  AccountBalanceWallet as WalletIcon,
  RequestPage as RequestPageIcon,
  MergeType as MergeTypeIcon,
  PointOfSale as PointOfSaleIcon,
  ArticleOutlined as ArticleOutlinedIcon,
  FilterNone as FilterNoneIcon,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
const Logo = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Tooltip title="Go to dashboard" arrow enterDelay={300}>
      <Box
        component={Link}
        to="/"
        aria-label="Go to dashboard"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: 0,
          p: 0.5,
          borderRadius: '10px',
          cursor: 'pointer',
          transition: 'background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
          '& img': { display: 'block', transition: 'opacity 160ms ease' },
          '&:hover': {
            backgroundColor: 'rgba(49, 0, 111, 0.06)',
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 6px rgba(49, 0, 111, 0.12)',
          },
          '&:hover img': { opacity: 0.92 },
          '&:active': { transform: 'translateY(0)' },
          '&:focus-visible': {
            outline: '2px solid rgba(49, 0, 111, 0.55)',
            outlineOffset: 2,
          },
        }}
      >
        <img alt="Logo" src={'./logo.webp'} height="40px" style={{ borderRadius: '8px' }} />
      </Box>
    </Tooltip>
  </Box>
);

const userMenuItemSx = {
  color: 'text.primary',
  '& .MuiListItemIcon-root': { color: 'text.secondary' },
};

const MyUserMenu = () => {
  return (
    <UserMenu>
      <MenuItemLink to="/profile" primaryText="Profile" leftIcon={<PersonIcon />} sx={userMenuItemSx} />
      <Logout sx={userMenuItemSx} />
    </UserMenu>
  );
};

export const MyAppBar = () => {
  return (
    <AppBar
      userMenu={<MyUserMenu />}
      sx={{
        backgroundColor: '#ffffff',
        color: 'text.primary',
        boxShadow: '0 2px 4px rgba(49, 0, 111, 0.08)',
        borderBottom: '2px solid #31006F',
      }}
    >
      {/* <Box sx={{ flex: '1' }} /> */}
      <TitlePortal />
      <Logo />
      <Box sx={{ flex: '1' }} />
    </AppBar>
  );
};

const MySidebar = (props: any) => {
  const [open] = useSidebarState();
  return (
    <Sidebar
      {...props}
      sx={{
        backgroundColor: '#31006F',
        color: '#ffffff',
        '& .RaSidebar-fixed': {
          backgroundColor: '#31006F',
          height: '100vh',
        },
        '& .MuiDrawer-root': {
          position: 'fixed',
        },
        '& .MuiDrawer-paper': {
          backgroundColor: '#31006F',
          width: open ? 261 : 56,
          height: '100vh',
          overflowY: 'auto !important',
          overflowX: 'hidden',
          transition: 'width 0.3s ease-in-out',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          },
        },
        // ─── Navigation links ───────────────────────────────────────────────
        '& .RaMenuItemLink-root': {
          color: 'rgba(255, 255, 255, 0.75) !important', // muted so the active item stands out
          py: 1,
          transition: 'background-color 150ms ease, color 150ms ease',
          '& .MuiListItemText-primary': {
            fontSize: '0.875rem',
            fontWeight: 500,
            letterSpacing: '0.01em',
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.07) !important',
            color: '#ffffff !important',
            '& .MuiListItemIcon-root': { color: '#ffffff !important' },
            '& .MuiListItemText-primary': { color: '#ffffff !important' },
          },
        },
        // ─── Collapsible group headers — larger + bolder so sections read as headers ──
        '& .MuiListItemButton-root': {
          py: 1,
          color: 'rgba(255, 255, 255, 0.95)',
          transition: 'background-color 150ms ease',
          '& .MuiListItemText-primary': {
            fontSize: '0.9375rem',
            fontWeight: 700,
            letterSpacing: '0.015em',
          },
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05) !important' },
        },
        // ─── Active item — subtle fill + brand-orange inset rule (echoes the icons) ──
        '& .RaMenuItemLink-root.RaMenuItemLink-active, & .RaMenuItemLink-root[aria-current="page"]': {
          backgroundColor: 'rgba(255, 255, 255, 0.16) !important',
          color: '#ffffff !important',
          boxShadow: 'inset 3px 0 0 0 #b54400',
          '& .MuiListItemIcon-root': { color: '#ffffff !important' },
          '& .MuiListItemText-primary': { color: '#ffffff !important', fontWeight: 600 },
        },
      }}
    />
  );
};
const MyMenu = () => {
  const location = useLocation();

  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [guardianUsersOpen, setGuardianUsersOpen] = useState(false);
  const [applicationManagementOpen, setApplicationManagementOpen] = useState(false);
  const [oneTimePaymentsOpen, setOneTimePaymentsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [salespalApplicationsOpen, setSalespalApplicationsOpen] = useState(false);
  const [manualMergesOpen, setManualMergesOpen] = useState(false);
  const [open] = useSidebarState();

  // Close all collapsible items when sidebar collapses
  useEffect(() => {
    if (!open) {
      setPaymentsOpen(false);
      setGuardianUsersOpen(false);
      setApplicationManagementOpen(false);
      setReportsOpen(false);
      setSalespalApplicationsOpen(false);
    }
  }, [open]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (open) {
      // Only auto-expand when sidebar is open
      // Check all paths that belong to Guardian Users section
      if (
        currentPath.includes('/client-users') ||
        currentPath.includes('/guardian-customers') ||
        currentPath.includes('/signin-attempt-history-logs') ||
        currentPath.includes('/blacklist-records') ||
        currentPath.includes('/max-signup-records') ||
        currentPath.includes('/signup-attempt-history-logs') ||
        currentPath.includes('/manual-merges')
        // // currentPath.includes('/ta-reports') ||
        // currentPath.includes('/order-records') ||
        // currentPath.includes('/non-online-renewals')
      ) {
        setGuardianUsersOpen(true);
      }
      // Check all paths that belong to Payments section
      if (currentPath.includes('/recurring-payments') || currentPath.includes('/recurring-payments-execution-logs') || currentPath.includes('/payment-cards')) {
        setPaymentsOpen(true);
      }
      // Check all paths that belong to Application Management section
      if (
        currentPath.includes('/application/customer-applications') ||
        currentPath.includes('/application/applicant-profiles') ||
        currentPath.includes('/application/risk-profiles') ||
        currentPath.includes('/application/documents') ||
        currentPath.includes('/application/docusign') ||
        currentPath.includes('/application/onfido-verifications')
      ) {
        setApplicationManagementOpen(true);
      }
      // Check all paths that belong to Reports section
      if (currentPath.includes('/admin/lapsing-policies')) {
        setReportsOpen(true);
      }
      if (currentPath.includes('/ta-reports') || currentPath.includes('/order-records') || currentPath.includes('/non-online-renewals') || currentPath.includes('/payment-logs') || currentPath.includes('/bank-cards') || currentPath.includes('/fac-records')) {
        setOneTimePaymentsOpen(true);
      }
      // Check all paths that belong to Salespal Applications section
      if (currentPath.includes('/salespal-applications')) {
        setSalespalApplicationsOpen(true);
      }
    }
  }, [location.pathname, open]);

  const handleResourceClick = () => {
    // setOpen(false);
  };

  const anySectionOpen = paymentsOpen || guardianUsersOpen || applicationManagementOpen || oneTimePaymentsOpen || reportsOpen || salespalApplicationsOpen || manualMergesOpen;

  // Toggle every section at once while keeping the sidebar itself open:
  // if anything is expanded, collapse all; otherwise expand all.
  const handleToggleAllSections = () => {
    const next = !anySectionOpen;
    setPaymentsOpen(next);
    setGuardianUsersOpen(next);
    setApplicationManagementOpen(next);
    setOneTimePaymentsOpen(next);
    setReportsOpen(next);
    setSalespalApplicationsOpen(next);
    setManualMergesOpen(next);
  };

  const handlePaymentsToggle = () => {
    setPaymentsOpen(!paymentsOpen);
  };

  const handleGuardianUsersToggle = () => {
    setGuardianUsersOpen(!guardianUsersOpen);
  };
  const handleSalespalApplicationsToggle = () => {
    setSalespalApplicationsOpen(!salespalApplicationsOpen);
  };
  const handleOneTimePayments = () => {
    setOneTimePaymentsOpen(!oneTimePaymentsOpen);
  };

  const handleApplicationManagementToggle = () => {
    setApplicationManagementOpen(!applicationManagementOpen);
  };

  const handleReportsToggle = () => {
    setReportsOpen(!reportsOpen);
  };

  return (
    <List component="nav" sx={{ width: '100%', maxWidth: 360, color: '#ffffff', pb: 10 }}>
      {/* ========== MAIN SECTION ========== */}
      <ListSubheader
        component="div"
        sx={{
          bgcolor: 'transparent',
          color: 'rgba(255,255,255,0.75)',
          fontWeight: 700,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 1,
          lineHeight: 2.5,
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: open ? 3 : 0,
          overflow: 'visible',
        }}
      >
        {open ? 'Main' : 'M'}
        {open && (
          <Tooltip title={anySectionOpen ? 'Collapse all sections' : 'Expand all sections'} arrow enterDelay={300}>
            <IconButton
              size="small"
              onClick={handleToggleAllSections}
              aria-label={anySectionOpen ? 'Collapse all sections' : 'Expand all sections'}
              sx={{
                color: 'rgba(255,255,255,0.75)',
                p: 0.5,
                flexShrink: 0,
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              <FilterNoneIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </ListSubheader>

      {/* Admin Users - Manage admin panel users and their access levels */}
      <CanAccess resource={resources.adminUsers} action="list">
        <MenuItemLink to={`/admin/admin-users`} primaryText="Users" leftIcon={<AdminPanelSettingsIcon sx={{ color: '#b54400' }} />} sx={{ color: '#ffffff' }} />
      </CanAccess>

      {/* Users Section - Collapsible menu for client and MDM user records */}
      <CanAccessAny resources={SECTION_RESOURCES.clientUsers}>
        <ListItemButton onClick={handleGuardianUsersToggle} sx={{ color: '#ffffff' }}>
          <ListItemIcon sx={{ minWidth: 40, color: '#b54400' }}>
            <PaymentIcon />
          </ListItemIcon>
          <ListItemText primary="Client Users" sx={{ color: '#ffffff', flex: 'none' }} />
          <ListItemIcon sx={{ minWidth: 'auto', color: '#b54400' }}>{guardianUsersOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}</ListItemIcon>
        </ListItemButton>
      </CanAccessAny>

      {/* C360 Login Records  */}
      <CanAccess resource={resources.clientUsers} action="list">
        <Collapse in={guardianUsersOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/client-users" primaryText="C360 Login Records" leftIcon={<PersonIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* C360 Signin Attempt History Logs  */}
      <CanAccess resource={resources.signinAttemptHistoryLogs} action="list">
        <Collapse in={guardianUsersOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/signin-attempt-history-logs" primaryText="Login Attempt History" leftIcon={<HistoryIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* MDM Records - Guardian Customers records */}
      <CanAccess resource={resources.guardianCustomers} action="list">
        <Collapse in={guardianUsersOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/guardian-customers" primaryText="MDM Records" leftIcon={<PersonIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* MDM Records - Blacklist Records records */}
      <CanAccess resource={resources.blacklistRecords} action="list">
        <Collapse in={guardianUsersOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/blacklist-records" primaryText="Blacklist Records" leftIcon={<SecurityIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* MDM Records - Max Sign-up Records records */}
      <CanAccess resource={resources.maxSignupRecords} action="list">
        <Collapse in={guardianUsersOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/max-signup-records" primaryText="Max Sign-up Records" leftIcon={<SecurityIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* MDM Records - Sign-up Attempt History Records */}
      <CanAccess resource={resources.signupAttemptHistoryLogs} action="list">
        <Collapse in={guardianUsersOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/signup-attempt-history-logs" primaryText="Sign-up Attempt History" leftIcon={<HistoryIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* SalesPal Manual Merge Records - lives under Client Users */}
      <CanAccess resource={resources.manualMerges} action="list">
        <Collapse in={guardianUsersOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/manual-merges" primaryText="Manual Merge Records" leftIcon={<MergeTypeIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* Policies - System policies and configuration rules */}
      <CanAccess resource={resources.policies} action="list">
        <MenuItemLink to="/admin/policies" primaryText="Policies" leftIcon={<PolicyIcon sx={{ color: '#b54400' }} />} sx={{ color: '#ffffff' }} />
      </CanAccess>

      {/* Salespal Applications - Salespal Applications records */}
      {/* Users Section - Collapsible menu for client and MDM user records */}
      <CanAccessAny resources={SECTION_RESOURCES.salespalApplications}>
        <ListItemButton onClick={handleSalespalApplicationsToggle} sx={{ color: '#ffffff' }}>
          <ListItemIcon sx={{ minWidth: 40, color: '#b54400' }}>
            <ArticleOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="Salespal Applications" sx={{ color: '#ffffff', flex: 'none' }} />
          <ListItemIcon sx={{ minWidth: 'auto', color: '#b54400' }}>{salespalApplicationsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}</ListItemIcon>
        </ListItemButton>
      </CanAccessAny>

      {/* C360 Salespal Applications Records  */}
      <CanAccess resource={resources.salespalApplications} action="list">
        <Collapse in={salespalApplicationsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/application/salespal-applications" primaryText="Applications" leftIcon={<RequestPageIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* one time payments section - collapsable menu for the tabs in section */}
      <CanAccessAny resources={SECTION_RESOURCES.paymentsAndRenewals}>
        <ListItemButton onClick={handleOneTimePayments} sx={{ color: '#ffffff' }}>
          <ListItemIcon sx={{ minWidth: 40, color: '#b54400' }}>
            <PointOfSaleIcon />
          </ListItemIcon>
          <ListItemText primary="Payments & Renewals" sx={{ color: '#ffffff', flex: 'none' }} />
          <ListItemIcon sx={{ minWidth: 'auto', color: '#b54400' }}>{oneTimePaymentsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}</ListItemIcon>
        </ListItemButton>
      </CanAccessAny>

      {/* Orders Records */}
      <CanAccess resource={resources.orderRecords} action="list">
        <Collapse in={oneTimePaymentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/order-records" primaryText="Orders Records" leftIcon={<CreditCardOffIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* Manual Payment Execution Logs */}
      <CanAccess resource={resources.manualPaymentExecutionLogs} action="list">
        <Collapse in={oneTimePaymentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/payment-logs" primaryText="Payment Logs" leftIcon={<WalletIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* MDM Records - Transaction Assurance Records records */}
      <CanAccess resource={resources.taReports} action="list">
        <Collapse in={oneTimePaymentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/ta-reports" primaryText="TA Records" leftIcon={<FindInPageIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* FAC Records */}
      <CanAccess resource={resources.facRecords} action="list">
        <Collapse in={oneTimePaymentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/fac-records" primaryText="FAC Records" leftIcon={<ReceiptLongIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* Bank Cards */}
      <CanAccess resource={resources.bankCards} action="list">
        <Collapse in={oneTimePaymentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/bank-cards" primaryText="Bank Cards" leftIcon={<CreditCardIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* MDM Records - Non Online Renewals records */}
      <CanAccess resource={resources.nonOnlineRenewals} action="list">
        <Collapse in={oneTimePaymentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/non-online-renewals" primaryText="Non Online Renewals" leftIcon={<SyncAltIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* Recurring Payments Section - Collapsible menu for payment automation */}
      <CanAccessAny resources={SECTION_RESOURCES.recurringPayments}>
        <ListItemButton onClick={handlePaymentsToggle} sx={{ color: '#ffffff' }}>
          <ListItemIcon sx={{ minWidth: 40, color: '#b54400' }}>
            <PaymentIcon />
          </ListItemIcon>
          <ListItemText primary="Recurring Payments" sx={{ color: '#ffffff', flex: 'none' }} />
          <ListItemIcon sx={{ minWidth: 'auto', color: '#b54400' }}>{paymentsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}</ListItemIcon>
        </ListItemButton>
      </CanAccessAny>

      {/* Configured Payments - Set up and manage recurring payment schedules */}
      <CanAccess resource={resources.recurringPayments} action="list">
        <Collapse in={paymentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/recurring-payments" primaryText="Configured Payments" leftIcon={<PaymentsIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* configured payment Cards */}
      <CanAccess resource={resources.paymentCards} action="list">
        <Collapse in={paymentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/payment-cards" primaryText="Configured Cards" leftIcon={<CreditCardIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* Execution History - View logs and history of recurring payment runs */}
      <CanAccess resource={resources.recurringPaymentsExecutionLogs} action="list">
        <Collapse in={paymentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItemLink to="/admin/recurring-payments-execution-logs" primaryText="Execution History" leftIcon={<HistoryIcon sx={{ color: '#b54400' }} />} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </List>
        </Collapse>
      </CanAccess>

      {/* Application Management Tree */}
      <CanAccessAny resources={SECTION_RESOURCES.applicationManagement}>
        <ListItemButton onClick={handleApplicationManagementToggle} sx={{ color: '#ffffff' }}>
          <ListItemIcon sx={{ minWidth: 40, color: '#b54400' }}>
            <DescriptionIcon />
          </ListItemIcon>
          <ListItemText primary="Application Management" sx={{ color: '#ffffff', flex: 'none' }} />
          <ListItemIcon sx={{ minWidth: 'auto', color: '#b54400' }}>{applicationManagementOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}</ListItemIcon>
        </ListItemButton>
      </CanAccessAny>

      <Collapse in={applicationManagementOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <CanAccess resource={resources.customerApplications} action="list">
            <MenuItemLink to="/application/customer-applications" primaryText="Applications" leftIcon={<AssignmentIcon sx={{ color: '#b54400' }} />} onClick={handleResourceClick} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </CanAccess>
          <CanAccess resource={resources.applicants} action="list">
            <MenuItemLink to="/application/applicant-profiles" primaryText="Applicants" leftIcon={<PersonAddIcon sx={{ color: '#b54400' }} />} onClick={handleResourceClick} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </CanAccess>
          <CanAccess resource={resources.riskProfiles} action="list">
            <MenuItemLink to="/application/risk-profiles" primaryText="Risk Profiles" leftIcon={<AssessmentIcon sx={{ color: '#b54400' }} />} onClick={handleResourceClick} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </CanAccess>
          <CanAccess resource={resources.documents} action="list">
            <MenuItemLink to="/application/documents" primaryText="Documents" leftIcon={<AssessmentIcon sx={{ color: '#b54400' }} />} onClick={handleResourceClick} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </CanAccess>
          <CanAccess resource={resources.docusign} action="list">
            <MenuItemLink to="/application/docusign" primaryText="DocuSign" leftIcon={<DescriptionIcon sx={{ color: '#b54400' }} />} onClick={handleResourceClick} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </CanAccess>
          <CanAccess resource={resources.onfidoVerifications} action="list">
            <MenuItemLink to="/application/onfido-verifications" primaryText="Onfido Verifications" leftIcon={<DescriptionIcon sx={{ color: '#b54400' }} />} onClick={handleResourceClick} sx={{ pl: open ? 4 : 2, color: '#ffffff' }} />
          </CanAccess>
        </List>
      </Collapse>

      {/* ========== SUPPORT SECTION ========== */}
      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
      <ListSubheader
        component="div"
        sx={{
          bgcolor: 'transparent',
          color: 'rgba(255,255,255,0.75)',
          fontWeight: 700,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 1,
          lineHeight: 2.5,
          textAlign: 'left',
        }}
      >
        {open ? 'Support Section' : 'SS'}
      </ListSubheader>

      {/* Roles and Permissions - Configure user roles and access control */}
      <CanAccess resource={resources.roles} action="list">
        <MenuItemLink to="/admin/roles" primaryText="Roles and Permissions" leftIcon={<BadgeIcon sx={{ color: '#b54400' }} />} sx={{ color: '#ffffff' }} />
      </CanAccess>

      {/* Audit Logs - View audit logs */}
      <CanAccess resource={resources.auditLogs} action="list">
        <MenuItemLink to="/admin/audit-logs" primaryText="Audit Logs" leftIcon={<HistoryIcon sx={{ color: '#b54400' }} />} sx={{ color: '#ffffff' }} />
      </CanAccess>

      {/* Knowledge Base - Troubleshooting guides and technical documentation */}
      <CanAccess resource={resources.troubleshootingGuidelines} action="list">
        <MenuItemLink to={`/admin/troubleshooting-guidelines`} primaryText="Knowledge Base" leftIcon={<BookIcon sx={{ color: '#b54400' }} />} sx={{ color: '#ffffff' }} />
      </CanAccess>

      {/* FAQs and User Manuals - Help documentation for end users */}
      {/* <MenuItemLink to={`/#`} primaryText="FAQs and User Manuals" leftIcon={<HelpIcon sx={{ color: '#b54400' }} />} sx={{ color: '#ffffff' }} /> */}
    </List>
  );
};

export const Layout = ({ children }: { children: ReactNode }) => {
  const isDevelopmentEnv = import.meta.env['NODE_ENV'] === 'development';
  return (
    <RALayout
      appBar={MyAppBar}
      sidebar={MySidebar}
      menu={MyMenu}
      sx={{
        // Lock the whole layout to the viewport so the page body never scrolls;
        // only the content pane does. Using flex (rather than a fixed calc) lets
        // the content fill exactly down to the bottom regardless of AppBar height.
        height: '100vh',
        overflow: 'hidden',
        '& .RaLayout-appFrame': {
          flex: 1,
          minHeight: 0,
        },
        '& .RaLayout-contentWithSidebar': {
          flex: 1,
          minHeight: 0,
        },
        '& .RaLayout-content': {
          backgroundColor: '#f7f5fb',
          // The content is the only scroll container and stretches to the bottom.
          overflowY: 'auto',
          minHeight: 0,
        },
      }}
    >
      {children}
      {isDevelopmentEnv && <ReactQueryDevtools initialIsOpen={false} />}
    </RALayout>
  );
};
